import { useLocation, useParams } from 'react-router-dom';
import { usePatientStore } from '../stores/usePatientStore';
import { useAppointmentStore } from '../stores/useAppointmentStore';
import { useMedicalRecordStore } from '../stores/useMedicalRecordStore';
import { usePatient } from './usePatients';
import { useEffect, useState } from 'react';

export interface PageContext {
  // Page identification
  currentPage: string;
  pageType: 'patient' | 'appointment' | 'medical-record' | 'lab-order' | 'prescription' | 'billing' | 'dashboard' | 'other';

  // Entity IDs
  patientId?: string;
  appointmentId?: string;
  medicalRecordId?: string;
  labOrderId?: string;
  prescriptionId?: string;

  // Entity data (light version for context)
  currentPatient?: {
    id: string;
    name: string;
    age?: number;
    gender?: string;
  };

  currentAppointment?: {
    id: string;
    date: string;
    reason?: string;
  };

  currentMedicalRecord?: {
    id: string;
    type?: string;
    visitDate?: string;
  };

  // Form state (if user is typing)
  isEditingForm?: boolean;
  formData?: Record<string, unknown>;

  // View state
  activeTab?: string;

  // Suggested actions based on page
  suggestedActions?: string[];
}

/**
 * Hook to extract current page context for AI assistance
 * Understands which page user is on and what entities are in view
 */
// UUID regex used to extract entity IDs from the URL when `useParams()`
// can't see them — i.e. when this hook is called from a component
// mounted at a parent route (e.g. AIAssistantBubble in MainLayout)
// where the child segment's params are out of scope. Without this
// fallback, the bubble's pageContext on /patients/:id reads
// pageType="other" and patientId=undefined, and the chat backend
// gets no patient context → the LLM has to bluff.
const UUID_RE_SRC = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';

function extractIdFromPath(pathname: string, segment: string): string | undefined {
  const m = pathname.match(new RegExp(`^/${segment}/(${UUID_RE_SRC})(?:/|$)`, 'i'));
  return m?.[1];
}

export const usePageContext = (): PageContext => {
  const location = useLocation();
  const params = useParams();
  const selectedPatient = usePatientStore(state => state.selectedPatient);
  const patients = usePatientStore(state => state.patients);
  const appointments = useAppointmentStore(state => state.appointments);
  const medicalRecords = useMedicalRecordStore(state => state.medicalRecords);

  // Resolve patientId from params (child-route scope) OR from the path
  // directly (parent-route scope — needed for AIAssistantBubble).
  const resolvedPatientId =
    (params.patientId && params.patientId !== 'new' ? params.patientId : undefined) ||
    extractIdFromPath(location.pathname, 'patients');

  // Fetch current patient from API if we're on a patient page
  const { data: patientData } = usePatient(resolvedPatientId || '');
  const currentPatientFromAPI = patientData?.data;

  const [context, setContext] = useState<PageContext>({
    currentPage: location.pathname,
    pageType: 'other',
  });

  useEffect(() => {
    const pathname = location.pathname;
    const newContext: PageContext = {
      currentPage: pathname,
      pageType: 'other',
    };

    // Parse route to determine page type and extract IDs

    // Patient pages
    if (pathname.startsWith('/patients')) {
      newContext.pageType = 'patient';

      // Patient details page: /patients/:patientId — but resolved
      // through resolvedPatientId so this works from MainLayout's
      // scope (where useParams() returns {}) as well as from inside
      // the matched route segment.
      if (resolvedPatientId) {
        const patientId = resolvedPatientId;

        // Use API data first (most accurate), fallback to store
        const patient = currentPatientFromAPI || patients.find(p => p.id === patientId) || selectedPatient;

        if (patient) {
          newContext.patientId = patient.id;
          newContext.currentPatient = {
            id: patient.id,
            name: getPatientName(patient),
            age: calculateAge(patient.dateOfBirth),
            gender: patient.gender,
          };

          newContext.suggestedActions = [
            'Schedule appointment',
            'View medical history',
            'Order lab tests',
            'Write prescription',
            'Analyze patient risk',
          ];
        } else {
          // Patient row not loaded yet — still surface the id so the
          // chat backend can build a context block without it.
          newContext.patientId = patientId;
        }
      } else if (pathname === '/patients') {
        // Patient list page
        newContext.suggestedActions = [
          'Add new patient',
          'Search patients',
          'View patient statistics',
        ];
      }
    }

    // Appointment pages
    else if (pathname.startsWith('/appointments')) {
      newContext.pageType = 'appointment';

      if (params.appointmentId && params.appointmentId !== 'new') {
        const appointmentId = params.appointmentId;
        const appointment = appointments.find(a => a.id === appointmentId);

        if (appointment) {
          newContext.appointmentId = appointment.id;
          newContext.currentAppointment = {
            id: appointment.id,
            date: appointment.date,
            reason: typeof appointment.reason === 'string' ? appointment.reason : appointment.reason?.en,
          };

          // Also get patient context
          const patient = patients.find(p => p.id === appointment.patientId);
          if (patient) {
            newContext.patientId = patient.id;
            newContext.currentPatient = {
              id: patient.id,
              name: getPatientName(patient),
              age: calculateAge(patient.dateOfBirth),
              gender: patient.gender,
            };
          }

          newContext.suggestedActions = [
            'Prepare for visit',
            'View patient history',
            'Start appointment recording',
            'Generate visit summary',
          ];
        }
      } else if (pathname === '/appointments') {
        newContext.suggestedActions = [
          'Schedule new appointment',
          'View today\'s schedule',
          'Optimize scheduling',
        ];
      }
    }

    // Medical Records pages
    else if (pathname.startsWith('/records') || pathname.startsWith('/medical-records')) {
      newContext.pageType = 'medical-record';

      if (params.recordId && params.recordId !== 'new') {
        const recordId = params.recordId;
        const record = medicalRecords.find(r => r.id === recordId);

        if (record) {
          newContext.medicalRecordId = record.id;
          newContext.currentMedicalRecord = {
            id: record.id,
            type: record.type,
            visitDate: record.date,
          };

          // Get patient context
          const patient = patients.find(p => p.id === record.patientId);
          if (patient) {
            newContext.patientId = patient.id;
            newContext.currentPatient = {
              id: patient.id,
              name: getPatientName(patient),
              age: calculateAge(patient.dateOfBirth),
              gender: patient.gender,
            };
          }

          newContext.suggestedActions = [
            'Analyze clinical notes',
            'Suggest diagnosis',
            'Generate treatment plan',
            'Check medication interactions',
          ];
        }
      } else if (pathname.includes('/new')) {
        // Adding new record
        newContext.isEditingForm = true;

        // Get patient from selected patient
        if (selectedPatient) {
          newContext.patientId = selectedPatient.id;
          newContext.currentPatient = {
            id: selectedPatient.id,
            name: getPatientName(selectedPatient),
            age: calculateAge(selectedPatient.dateOfBirth),
            gender: selectedPatient.gender,
          };
        }

        newContext.suggestedActions = [
          'Auto-complete SOAP note',
          'Suggest diagnosis',
          'Generate treatment plan',
          'Voice dictation',
        ];
      }
    }

    // Lab Orders pages
    else if (pathname.startsWith('/lab-orders')) {
      newContext.pageType = 'lab-order';

      if (params.orderId && params.orderId !== 'new') {
        newContext.labOrderId = params.orderId;
        newContext.suggestedActions = [
          'Interpret lab results',
          'Compare to previous results',
          'Suggest follow-up tests',
          'Generate patient report',
        ];
      } else if (pathname.includes('/new')) {
        newContext.isEditingForm = true;
        newContext.suggestedActions = [
          'Suggest test panels',
          'Check appropriateness',
          'Estimate turnaround time',
        ];
      } else {
        newContext.suggestedActions = [
          'Order new lab tests',
          'Review pending results',
          'Flag critical values',
        ];
      }
    }

    // Prescription pages
    else if (pathname.startsWith('/prescriptions')) {
      newContext.pageType = 'prescription';

      if (params.prescriptionId && params.prescriptionId !== 'new') {
        newContext.prescriptionId = params.prescriptionId;
        newContext.suggestedActions = [
          'Check drug interactions',
          'Review dosage',
          'Suggest alternatives',
          'Patient education points',
        ];
      } else if (pathname.includes('/new')) {
        newContext.isEditingForm = true;
        newContext.suggestedActions = [
          'Suggest medications',
          'Calculate dosage',
          'Check interactions',
          'Generic alternatives',
        ];
      }
    }

    // Billing pages
    else if (pathname.startsWith('/billing')) {
      newContext.pageType = 'billing';
      newContext.suggestedActions = [
        'Suggest ICD-10 codes',
        'Suggest CPT codes',
        'Check documentation completeness',
        'Optimize reimbursement',
      ];
    }

    // Dashboard
    else if (pathname === '/dashboard' || pathname === '/') {
      newContext.pageType = 'dashboard';
      newContext.suggestedActions = [
        'View today\'s schedule',
        'Review pending tasks',
        'Check critical values',
        'View patient alerts',
      ];
    }

    setContext(newContext);
  }, [location.pathname, params, resolvedPatientId, selectedPatient, patients, appointments, medicalRecords, currentPatientFromAPI]);

  return context;
};

/**
 * Helper to get patient name from various formats
 */
function getPatientName(patient: { name?: string | { en?: string }; firstName?: string; lastName?: string }): string {
  if (typeof patient.name === 'string') {
    return patient.name;
  }
  if (patient.name?.en) {
    return patient.name.en;
  }
  if (patient.firstName && patient.lastName) {
    return `${patient.firstName} ${patient.lastName}`;
  }
  return 'Unknown';
}

/**
 * Helper to calculate age from date of birth
 */
function calculateAge(dateOfBirth: string | undefined): number | undefined {
  if (!dateOfBirth) return undefined;

  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  return age;
}
